import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex panel-stake flex-column sm:justify-content-between sm:flex-row xl:flex-column" }
const _hoisted_2 = { class: "flex-grow-1" }
const _hoisted_3 = { class: "caption mb-s2" }
const _hoisted_4 = { class: "xl:w-12 sm:w-4 sm:pl-s3 xl:p-s0" }

import BigNumber from 'bignumber.js';
import { useI18n } from 'vue-i18n';
import { computed, ref, toRef, watch, nextTick } from 'vue';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';
import { BIG_ZERO } from '@/utils/bigNumber';
import { STEP_STATUS } from '@/components/stepper/stepper.types';
import { toWei } from '@/sdk/utils';
import { format } from '@/utils/format';
import { Pair } from '@/sdk/entities/pair';
import { Token } from '@/sdk/entities/token';
import { PortfolioFarm } from '@/sdk/entities/portfolioFarm';
import { FarmSourceType } from '@/sdk/entities/farmSource';
import Spinner from '@/components/Spinner.vue';
import HarvestConfirmation from '../confirmation/HarvestConfirmation.vue';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { useWallet } from '@/store/modules/wallet/useWallet';
import { useAllSteps } from '@/composables/useAllSteps';
import { useFarmingSteps } from '@/store/modules/farming/useFarmingSteps';
import { useFarmingHarvest } from '@/store/modules/farming/harvest/useFarmingHarvest';
import { useFarmingHarvestSteps } from '@/store/modules/farming/harvest/useFarmingHarvestSteps';
import { useMilkomedaWSCBalanceADAIntoCardanoValidator } from '@/composables/milkomeda-wrapped-smartcontract/useMilkomedaWSCBalanceADAIntoCardanoValidator';


export default /*#__PURE__*/_defineComponent({
  __name: 'Harvest',
  props: {
    farm: null,
    tokens: null,
    wscAdaValidator: null
  },
  emits: ["sortFarms", "sendTransaction"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'sortFarms'): void;
  (e: 'sendTransaction'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  farm: PortfolioFarm;
  tokens: Pair | Token[];
  wscAdaValidator: ReturnType<typeof useMilkomedaWSCBalanceADAIntoCardanoValidator>;
}





const { t } = useI18n();
const { getTokenByAddressAndChainId } = useTokens();
const { walletState } = useWallet();
const { farmingSteps, setFarmingAction, $reset: resetFarmingSteps } = useFarmingSteps();
const { farmingHarvestForm, checkHarvestForBridgeFromMilkomeda, setHarvestFromFarm, doHarvest } =
  useFarmingHarvest();
const { farmingHarvestSteps } = useFarmingHarvestSteps();
const allStepsState = useAllSteps();

const isShowDialog = ref(false);
const isShowBtn = ref(true);
const inProgress = ref(false);

const isValidCardanoBalanceForHarvestRef = toRef(
  props.wscAdaValidator.milkomedaWSCBalanceADAIntoCardanoValidatorState,
  'isValidCardanoBalance',
);
const minADABalanceIntoCardanoForHarvestRef = toRef(
  props.wscAdaValidator.milkomedaWSCBalanceADAIntoCardanoValidatorState,
  'minADABalance',
);

const isSyrup = computed(() => props.farm.isSyrupPool);
const isNotEnoughADA = computed(
  () => ENABLE_FAKE_CARDANO_NETWORK && !isValidCardanoBalanceForHarvestRef.value,
);
const isSuccessfulHarvestSteps = computed(() => {
  if (!farmingHarvestSteps.steps.length) return false;
  if (farmingSteps.action !== 'harvest') return false;

  return (
    !farmingSteps.inProgress &&
    farmingHarvestForm.farm?.farm === props.farm.farm &&
    farmingHarvestSteps.steps.at(-1)?.status === STEP_STATUS.SUCCESS
  );
});
const disableButton = computed(() => {
  return (
    !walletState.isInjected ||
    !props.farm.earned.gt(BIG_ZERO) ||
    inProgress.value ||
    allStepsState.isInProgress.state
  );
});
const harvestToken = computed(() => {
  if (!DEFAULT_NETWORK_ID) return;

  return getTokenByAddressAndChainId(props.farm.tokenToMint, +DEFAULT_NETWORK_ID);
});
const earnedToken = computed(() => {
  if (!harvestToken.value) return;
  if (!ENABLE_FAKE_CARDANO_NETWORK && harvestToken.value.isWETHToken()) {
    return harvestToken.value.unwrapWETH();
  }

  return harvestToken.value;
});

function resetForm() {
  emits('sortFarms');
}
function onDialogOpen() {
  inProgress.value = false;
  isShowDialog.value = true;
}
function onDialogClose() {
  isShowDialog.value = false;
}
// CONFIRM HARVEST
async function onConfirmWSCAction() {
  if (farmingSteps.isShown) {
    resetFarmingSteps();
  }

  setFarmingAction('harvest');

  const token = harvestToken.value!;
  const amountInWei = toWei(props.farm.printEarned.toFixed(), token.decimals).toString();
  checkHarvestForBridgeFromMilkomeda(amountInWei, token);

  setHarvestFromFarm(props.farm);

  await nextTick();

  emits('sendTransaction');
}
async function onConfirmAction() {
  inProgress.value = true;

  setFarmingAction('harvest');
  setHarvestFromFarm(props.farm);

  try {
    await doHarvest();
    resetForm();
  } finally {
    inProgress.value = false;
  }
}
async function onConfirm() {
  onDialogClose();

  if (ENABLE_FAKE_CARDANO_NETWORK) {
    await onConfirmWSCAction();
    return;
  }

  await onConfirmAction();
}
function isPairFarm() {
  return props.farm.sourceType === FarmSourceType.PAIR;
}
function getDialogHeader() {
  return isPairFarm()
    ? t('farming.harvestConfirmDialog.header', {
        farmName: `${(props.tokens as Pair).token0.symbol} + ${
          (props.tokens as Pair).token1.symbol
        }`,
      })
    : t('farming.harvestConfirmDialog.header', { farmName: props.farm.portfolio.name });
}

watch(isSuccessfulHarvestSteps, isSuccessful => {
  if (isSuccessful) {
    resetForm();
  }
});

watch(
  [() => props.farm.printEarned.toFixed(), harvestToken],
  ([amount, token]) => {
    if (!BigNumber(amount).isZero() && token) {
      const amountInWei = toWei(amount, token.decimals).toString();
      props.wscAdaValidator.checkBalanceADAForBridgeFromMilkomeda(amountInWei, token);
    }
  },
  { immediate: true },
);

return (_ctx: any,_cache: any) => {
  const _component_Button = _resolveComponent("Button")!
  const _component_Dialog = _resolveComponent("Dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)('farming.youEarned.caption')), 1),
      _createElementVNode("div", null, _toDisplayString(`${_unref(format)(__props.farm.printEarned.toSignificant(6))} ${_unref(earnedToken)?.symbol}`), 1)
    ]),
    _createElementVNode("div", _hoisted_4, [
      (isShowBtn.value)
        ? (_openBlock(), _createBlock(_component_Button, {
            key: 0,
            class: "w-full button-primary button-lg mt-s5 sm:mt-s0",
            disabled: _unref(disableButton),
            onClick: onDialogOpen
          }, {
            default: _withCtx(() => [
              (inProgress.value)
                ? (_openBlock(), _createBlock(Spinner, {
                    key: 0,
                    color: "white",
                    size: "24",
                    class: "mr-s2"
                  }))
                : _createCommentVNode("", true),
              _createTextVNode(" " + _toDisplayString(inProgress.value ? _unref(t)('inProgress') : _ctx.$t('farming.harvestEarnings')), 1)
            ]),
            _: 1
          }, 8, ["disabled"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_Dialog, {
      visible: isShowDialog.value,
      "onUpdate:visible": _cache[0] || (_cache[0] = ($event: any) => ((isShowDialog).value = $event)),
      header: getDialogHeader(),
      dismissableMask: "",
      modal: true
    }, {
      default: _withCtx(() => [
        _createVNode(HarvestConfirmation, {
          earned: __props.farm.printEarned.toSignificant(6),
          earnedUsd: __props.farm.printEarnedUSD.toFixed(),
          cancel: onDialogClose,
          confirm: onConfirm,
          "in-progress": inProgress.value,
          "is-syrup": _unref(isSyrup),
          token: _unref(earnedToken)!,
          "is-not-available": _unref(isNotEnoughADA),
          "min-amount-for-fee": _unref(minADABalanceIntoCardanoForHarvestRef)
        }, null, 8, ["earned", "earnedUsd", "in-progress", "is-syrup", "token", "is-not-available", "min-amount-for-fee"])
      ]),
      _: 1
    }, 8, ["visible", "header"])
  ]))
}
}

})